import { AgFilterComponent } from '@ag-grid-community/angular';
import { HttpClient } from '@angular/common/http';
import { IDoesFilterPassParams, IFilterParams } from '@ag-grid-community/core';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	ViewEncapsulation
} from '@angular/core';
import { debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { term, toString as luceneToString, FieldExpression } from 'lucene';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

import { FilterInstance } from '../../services/datacenter-filter';
import { TranslationService } from '../../services/translation.service';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { LoadingSpinnerComponent } from '../../components/utils/loading-spinner.component';
import { PaginatedResult } from '../../models/paginated-result';
import { DropdownFilter, FilterType } from '../../models/ag-grid';

interface DatacenterFilterParamsInterface extends IFilterParams {
	orderFields: [];
	batchFields: [];
	saOrderFields: [];
	url: string;
}

@Component({
	selector: 'copo-datacenter-suggestive-list-filter',
	templateUrl: './datacenter-suggestive-list-filter.component.html',
	styleUrl: './datacenter-suggestive-list-filter.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatButtonModule,
		MatFormFieldModule,
		MatIconModule,
		LoadingSpinnerComponent,
		MatSelectModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		AsyncPipe,
		MatOptionModule,
		MatInputModule,
		MatAutocompleteModule,
	]
})
export class DatacenterSuggestiveListFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	header: string | undefined;
	filteredItems?: Observable<{ code?: string | undefined; translation?: string }[]>;
	isLoading = false;
	datacenterType: DatacenterType = 'order';
	searchTextControl = new FormControl();
	filterValue: { code?: string | null; translation?: string | null } = {};
	datacenterTypeSub?: Subscription;
	fields: string[] = [];
	url?: string;
	private isClientSide = false;
	private params?: DatacenterFilterParamsInterface;
	private readonly textSub?: Subscription;
	private readonly cache = new Map<string, { code?: string | undefined; translation: string }[]>();
	private readonly NO_VALUE_CODE = 'novalue';

	constructor(
		private readonly route: ActivatedRoute,
		private readonly http: HttpClient,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly translationService: TranslationService,
	) {
		this.datacenterTypeSub = this.route.data.subscribe(res => {
			this.datacenterType = <DatacenterType>res['type'];
		});
		this.filteredItems = this.searchTextControl.valueChanges
			.pipe(
				startWith(this.searchTextControl.value),
				debounceTime(300),
				distinctUntilChanged(),
				tap((searchText: string) => {
					this.isLoading = true;
					this.changeDetectorRef.markForCheck();
					this.filterValue.translation = searchText;
				}),
				switchMap((query: string) => this.getItemBySearchText(query)),
				tap(() => {
					this.isLoading = false;
					this.changeDetectorRef.markForCheck();
				})
			);
	}

	agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		this.header = this.params.colDef.headerName;
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
		switch (this.datacenterType) {
		case 'order':
			this.fields = this.params.orderFields;
			break;
		case 'calloff-by-volume':
		case 'calloff-by-batch':
		case 'batch':
			this.fields = this.params.batchFields;
			break;
		case 'schedule-business-order':
			this.fields = this.params.saOrderFields;
			break;
		default:
			break;
		}
	}

	isFilterActive() {
		return this.filterValue.code !== undefined && this.filterValue.code !== null;
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		if (!this.isFilterActive()) {
			return true;
		}
		if (!this.params || typeof filterParams.data !== 'object') {
			return false;
		}
		const [codeField, translationField] = this.fields;
		const { data } = filterParams;
		const rawCode = codeField ? data[codeField] : undefined;
		const rawTranslation = translationField ? data[translationField] : undefined;
		const codeValue = typeof rawCode === 'string' || typeof rawCode === 'number' ? String(rawCode) : '';
		const translationValue =
			typeof rawTranslation === 'string' || typeof rawTranslation === 'number' ? String(rawTranslation) : '';
		const selectedCode = String(this.filterValue.code ?? '');
		const selectedTranslation = String(this.filterValue.translation ?? '');
		return codeValue === selectedCode || translationValue === selectedTranslation;
	}

	getModel(): DropdownFilter | undefined {
		const { code } = this.filterValue;
		if (code === null || code === undefined) {
			return undefined;
		}
		return {
			customFilterName: FilterType.SUGGESTIVE_LIST_FILTER,
			text: code === this.NO_VALUE_CODE ? '' : code
		};
	}

	setModel(code: string | null) {
		this.filterValue = {};
		if (code === null) {
			this.filterValue = { code: null, translation: null };
			this.searchTextControl.reset(null, { emitEvent: false });
			return;
		}
		if (code === '' || code === this.NO_VALUE_CODE) {
			const label = this.translationService.getTR('filter.no-value');
			this.filterValue = { code: this.NO_VALUE_CODE, translation: label };
			this.searchTextControl.setValue(label, { emitEvent: false });
			return;
		}

		this.filterValue = { code, translation: code };
		this.searchTextControl.setValue(code, { emitEvent: false });
	}

	getCustomFilterName(): FilterType.SUGGESTIVE_LIST_FILTER {
		return FilterType.SUGGESTIVE_LIST_FILTER;
	}

	ngOnDestroy() {
		if (this.datacenterTypeSub) {
			this.datacenterTypeSub.unsubscribe();
		}
		if (this.textSub) {
			this.textSub.unsubscribe();
		}
	}

	propositionChanged(proposition: { code?: string; translation?: string }) {
		Object.assign(this.filterValue, proposition);
		this.searchTextControl.setValue(this.filterValue.translation);
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	resetFilter() {
		this.searchTextControl.reset();
		this.searchTextControl.setValue(null);
		this.filterValue = {};
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	getItemBySearchText(searchText: string) {
		const filterValue = searchText ? {
			field: this.fields[1],
			term: `*${ term.escape(searchText) }*`,
			quoted: false
		} : undefined;

		return this.sendRequest(filterValue);
	}

	private getPropertyFromObject(value: Record<string, string | Record<string, string>>, apiName: string) {
		const [firstElement, secondElement] = apiName.split('.');
		if (secondElement) {
			return (<Record<string, string>> value[firstElement])[secondElement];
		}
		return <string> value[firstElement];
	}

	private normalizeNoValue(raw: string | undefined | null) {
		const v = (raw ?? '').trim();
		if (v.length === 0) {
			return {
				code: this.NO_VALUE_CODE,
				translation: this.translationService.getTR('filter.no-value'),
			};
		}
		return { code: v, translation: v };
	}

	private sendRequest(filterVal?: FieldExpression) {
		if (!this.params) {
			return of([]);
		}

		this.url = this.params.url;
		const url = new URL(this.url);
		const params = url.searchParams;
		params.append('fields', this.fields[0]
			? `${ this.fields[0] }, ${ this.fields[1] }`
			: this.fields[1]);

		params.append('orderBy', this.fields[1]);

		if (filterVal) {
			params.append('filter', luceneToString(filterVal));
		}

		this.setParameters(params);

		const cacheResult = this.cache.get(url.toString());
		if (cacheResult) {
			return of(cacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, string>> | Record<string, string>[]>(url.toString())
			.pipe(
				map(values => {
					const array = 'results' in values ? values.results : values;
					return array.map(value => {
						const rawCode = this.fields[0] ? this.getPropertyFromObject(value, this.fields[0]) : undefined;
						const rawTranslation = this.getPropertyFromObject(value, this.fields[1]);
						const codeNorm = this.normalizeNoValue(rawCode);
						const trNorm = this.normalizeNoValue(rawTranslation);
						const isNoValue = codeNorm.code === this.NO_VALUE_CODE || trNorm.code === this.NO_VALUE_CODE;

						return {
							code: this.fields[0]
								? isNoValue ? this.NO_VALUE_CODE : (rawCode ?? '').trim()
								: undefined,
							translation: isNoValue
								? this.translationService.getTR('filter.no-value')
								: rawTranslation.trim(),
						};
					});
				}),
				tap(result => {
					this.cache.set(url.toString(), result);
				})
			);
	}

	private setParameters(searchParams: URLSearchParams) {
		searchParams.append('distinct', 'true');
		searchParams.append('limit', '1500');
	}
}
